import React, { memo, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { append, remove, toLower, isEmpty, filter, includes, trim, pipe } from 'ramda';
import { Box, CircularProgress, Checkbox, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SearchField } from '@alycecom/ui';

import { FilterItemShape } from '../../shapes';
import { findItemIndexById } from '../../../../helpers/list.helpers';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    maxHeight: 270,
    overflowY: 'auto',
  },
  listOption: {
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  listOptionText: {
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: palette.text.primary,
  },
  listIcon: {
    justifyContent: 'center',
  },
  loadIcon: {
    marginRight: spacing(1),
  },
  checkbox: {
    color: `${palette.customStyling.backgroundColor} !important`,
  },
}));

const prepareValue = pipe(trim, toLower);
const filterOptions = value => filter(item => includes(value, prepareValue(item.label)));

const MobileFilterItemsList = ({ label, name, value, options, withSearch, isLoading, onChange }) => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  useDebounce(() => setDebouncedSearch(search), 300, [search]);

  const filtredOptions = useMemo(() => {
    const searchValue = prepareValue(debouncedSearch);
    if (!withSearch || isEmpty(searchValue)) {
      return options;
    }
    return filterOptions(searchValue)(options);
  }, [options, debouncedSearch, withSearch]);

  const handleSelectItem = useCallback(
    (index, checked, option) => {
      const selectedValues = checked ? remove(index, 1, value) : append(option, value);
      onChange(name, selectedValues, option, !checked);
    },
    [onChange, value, name],
  );

  const handleSearch = useCallback(event => setSearch(event.target.value), []);

  const hasOptions = !!filtredOptions.length;

  return (
    <Box>
      {withSearch && (
        <Box mx={3} mb={2}>
          <SearchField
            placeholder={`Search ${toLower(label)}`}
            value={search}
            onChange={handleSearch}
            closeIconColor="customStyling.highlightColor"
            disabled={isLoading}
          />
        </Box>
      )}
      {isLoading && (
        <Box
          width={250}
          height={60}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          pl={2}
          my={2}
          color="customStyling.primaryColor"
        >
          <CircularProgress className={classes.loadIcon} color="inherit" size={32} />
          <Typography className="Body-Regular-Left-Static">Loading...</Typography>
        </Box>
      )}
      {!hasOptions && !isLoading && (
        <Box my={2} textAlign="center">
          No options.
        </Box>
      )}
      {hasOptions && (
        <>
          <List className={classes.root}>
            {filtredOptions.map(option => {
              const labelId = `checkbox-list-label-${option.id}`;
              const labelText = `${option.label} ${option.productsCount > 0 ? `(${option.productsCount})` : ''}`;
              const optionIndex = findItemIndexById(option.id)(value);
              const checked = optionIndex !== -1;
              return (
                <ListItem
                  classes={{
                    dense: classes.listOption,
                  }}
                  key={option.id}
                  role={undefined}
                  dense
                  button
                  onClick={() => handleSelectItem(optionIndex, checked, option)}
                >
                  <ListItemIcon
                    classes={{
                      root: classes.listIcon,
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={checked}
                      tabIndex={-1}
                      disableRipple
                      color="primary"
                      classes={{ root: classes.checkbox }}
                      inputProps={{ 'aria-labelledby': labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    classes={{
                      primary: classes.listOptionText,
                    }}
                    className="Body-Regular-Left-Static"
                    id={labelId}
                    primary={labelText}
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </Box>
  );
};

MobileFilterItemsList.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(FilterItemShape).isRequired,
  value: PropTypes.arrayOf(FilterItemShape),
  isLoading: PropTypes.bool,
  withSearch: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

MobileFilterItemsList.defaultProps = {
  isLoading: false,
  withSearch: true,
  value: [],
};

export default memo(MobileFilterItemsList);
